import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  DndContext,
  DragOverlay,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  pointerWithin,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import type {
  Announcements,
  CollisionDetection,
  DragEndEvent,
  DragStartEvent,
  KeyboardCodes,
  KeyboardCoordinateGetter,
} from "@dnd-kit/core";
import { twMerge } from "tailwind-merge";

/** The `md` breakpoint of tailwind.config.js. Below it, columns stack. */
export const KANBAN_DND_MIN_WIDTH = 768;

/** Pixels the pointer must travel before a drag starts, so a click stays a click. */
const ACTIVATION_DISTANCE = 5;

/**
 * Space picks a card up and drops it, arrows move it, Escape cancels. Enter is
 * deliberately left out: on every board it already opens the card detail.
 */
const KEYBOARD_CODES: KeyboardCodes = {
  start: ["Space"],
  cancel: ["Escape"],
  end: ["Space"],
};

const ARROW_CODES = ["ArrowRight", "ArrowLeft", "ArrowDown", "ArrowUp"];

const isIgnoredTarget = (target: EventTarget | null): boolean => {
  const element = target as HTMLElement | null;
  return !!element?.closest?.("[data-dnd-ignore]");
};

/** A pointer sensor that refuses to start on a control marked `data-dnd-ignore`. */
class KanbanPointerSensor extends PointerSensor {
  static activators = [
    {
      eventName: "onPointerDown" as const,
      handler: ({ nativeEvent: event }: React.PointerEvent) => {
        if (!event.isPrimary || event.button !== 0) return false;
        if (isIgnoredTarget(event.target)) return false;
        return true;
      },
    },
  ];
}

const kanbanCoordinateGetter: KeyboardCoordinateGetter = (
  event,
  { context }
) => {
  if (!ARROW_CODES.includes(event.code)) return undefined;
  const { collisionRect, droppableContainers, droppableRects } = context;
  if (!collisionRect) return undefined;
  event.preventDefault();

  const fromX = collisionRect.left + collisionRect.width / 2;
  const fromY = collisionRect.top + collisionRect.height / 2;

  let best: { x: number; y: number; distance: number } | null = null;

  for (const container of droppableContainers.getEnabled()) {
    const rect = droppableRects.get(container.id);
    if (!rect) continue;
    const dx = rect.left + rect.width / 2 - fromX;
    const dy = rect.top + rect.height / 2 - fromY;
    const reachable =
      event.code === "ArrowRight"
        ? dx > 1
        : event.code === "ArrowLeft"
        ? dx < -1
        : event.code === "ArrowDown"
        ? dy > 1
        : dy < -1;
    if (!reachable) continue;
    const distance = Math.hypot(dx, dy);
    if (best !== null && distance >= best.distance) continue;
    best = {
      x: rect.left + (rect.width - collisionRect.width) / 2,
      y: rect.top + Math.max(0, (rect.height - collisionRect.height) / 2),
      distance,
    };
  }

  if (best === null) return undefined;
  return { x: best.x, y: best.y };
};

/** Pointer first so the column under the cursor wins, centres for the keyboard. */
const collisionDetection: CollisionDetection = (args) => {
  const pointerCollisions = args.pointerCoordinates ? pointerWithin(args) : [];
  return pointerCollisions.length > 0 ? pointerCollisions : closestCenter(args);
};

/**
 * Screen reader wording. Every board passes its own already translated strings:
 * the shared component holds no translation key, and the board is the only place
 * that can turn a card id into a readable name.
 */
export interface KanbanDndTexts {
  /** Read when a card takes focus, explains how to move it. */
  instructions: string;
  onDragStart: (cardId: string) => string;
  onDragOver: (cardId: string, columnId: string | null) => string;
  onDragEnd: (cardId: string, columnId: string | null) => string;
  onDragCancel: (cardId: string) => string;
}

export interface KanbanDndProps {
  onMoveCard: (cardId: string, targetColumnId: string) => void;
  disabled?: boolean;
  renderDragOverlay: (cardId: string) => React.ReactNode;
  texts?: KanbanDndTexts;
  onDraggingChange?: (dragging: boolean) => void;
  minWidth?: number;
  children: React.ReactNode;
}

interface KanbanDndState {
  enabled: boolean;
}

const KanbanDndStateContext = createContext<KanbanDndState>({
  enabled: false,
});

/**
 * True when the viewport is wide enough for the columns to sit side by side.
 * Boards use it to decide whether to keep their small screen fallback control.
 */
export const useKanbanDndEnabled = (
  minWidth: number = KANBAN_DND_MIN_WIDTH
): boolean => {
  const query = `(min-width: ${minWidth}px)`;
  // Measured on the first render, not in the effect: waiting a frame would show
  // the small screen fallback for one paint on every desktop load.
  const [enabled, setEnabled] = useState(
    () =>
      typeof window !== "undefined" &&
      typeof window.matchMedia === "function" &&
      window.matchMedia(query).matches
  );

  useEffect(() => {
    if (
      typeof window === "undefined" ||
      typeof window.matchMedia !== "function"
    )
      return;
    const mediaQuery = window.matchMedia(query);
    const update = () => setEnabled(mediaQuery.matches);
    update();
    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", update);
      return () => mediaQuery.removeEventListener("change", update);
    }
    mediaQuery.addListener(update);
    return () => mediaQuery.removeListener(update);
  }, [query]);

  return enabled;
};

export const KanbanDnd = ({
  onMoveCard,
  disabled = false,
  renderDragOverlay,
  texts,
  onDraggingChange,
  minWidth = KANBAN_DND_MIN_WIDTH,
  children,
}: KanbanDndProps) => {
  const wideEnough = useKanbanDndEnabled(minWidth);
  const enabled = wideEnough && !disabled;
  const [activeCardId, setActiveCardId] = useState<string | null>(null);

  const draggingRef = useRef(false);
  const onDraggingChangeRef = useRef(onDraggingChange);
  useEffect(() => {
    onDraggingChangeRef.current = onDraggingChange;
  }, [onDraggingChange]);

  const reportDragging = useCallback((dragging: boolean) => {
    if (draggingRef.current === dragging) return;
    draggingRef.current = dragging;
    onDraggingChangeRef.current?.(dragging);
  }, []);

  useEffect(() => {
    if (enabled) return;
    setActiveCardId(null);
    reportDragging(false);
  }, [enabled, reportDragging]);

  useEffect(() => () => reportDragging(false), [reportDragging]);

  const sensors = useSensors(
    useSensor(KanbanPointerSensor, {
      activationConstraint: { distance: ACTIVATION_DISTANCE },
    }),
    useSensor(KeyboardSensor, {
      keyboardCodes: KEYBOARD_CODES,
      coordinateGetter: kanbanCoordinateGetter,
    })
  );

  const handleDragStart = useCallback(
    (event: DragStartEvent) => {
      setActiveCardId(String(event.active.id));
      reportDragging(true);
    },
    [reportDragging]
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveCardId(null);
      reportDragging(false);
      const { active, over } = event;
      if (!over) return;
      const targetColumnId = String(over.id);
      const sourceColumnId = active.data.current?.columnId;
      if (sourceColumnId != null && String(sourceColumnId) === targetColumnId)
        return;
      onMoveCard(String(active.id), targetColumnId);
    },
    [onMoveCard, reportDragging]
  );

  const handleDragCancel = useCallback(() => {
    setActiveCardId(null);
    reportDragging(false);
  }, [reportDragging]);

  const announcements = useMemo<Announcements | undefined>(() => {
    if (!texts) return undefined;
    return {
      onDragStart: ({ active }) => texts.onDragStart(String(active.id)),
      onDragOver: ({ active, over }) =>
        texts.onDragOver(String(active.id), over ? String(over.id) : null),
      onDragEnd: ({ active, over }) =>
        texts.onDragEnd(String(active.id), over ? String(over.id) : null),
      onDragCancel: ({ active }) => texts.onDragCancel(String(active.id)),
    };
  }, [texts]);

  const contextValue = useMemo(() => ({ enabled }), [enabled]);

  if (!enabled) {
    return (
      <KanbanDndStateContext.Provider value={contextValue}>
        {children}
      </KanbanDndStateContext.Provider>
    );
  }

  return (
    <KanbanDndStateContext.Provider value={contextValue}>
      <DndContext
        sensors={sensors}
        collisionDetection={collisionDetection}
        accessibility={
          texts
            ? {
                announcements,
                screenReaderInstructions: { draggable: texts.instructions },
              }
            : undefined
        }
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        onDragCancel={handleDragCancel}
      >
        {children}
        <DragOverlay dropAnimation={null}>
          {activeCardId ? renderDragOverlay(activeCardId) : null}
        </DragOverlay>
      </DndContext>
    </KanbanDndStateContext.Provider>
  );
};

export interface KanbanColumnProps {
  columnId: string;
  /** Classes of the column surface. Composed with `twMerge`. */
  className?: string;
  /** Applied on top while a card hovers the column. */
  overClassName?: string;
  children: React.ReactNode;
}

/**
 * The whole column is the drop target, header included, so an empty column
 * stays reachable as long as it keeps a minimum height.
 */
export const KanbanColumn = ({
  columnId,
  className,
  overClassName = "border-blue-300 bg-blue-50",
  children,
}: KanbanColumnProps) => {
  const { enabled } = useContext(KanbanDndStateContext);
  const { setNodeRef, isOver } = useDroppable({
    id: columnId,
    disabled: !enabled,
  });

  return (
    <div
      ref={setNodeRef}
      className={twMerge(
        "border border-transparent transition-colors",
        className,
        enabled && isOver ? overClassName : undefined
      )}
    >
      {children}
    </div>
  );
};

export interface KanbanCardProps {
  cardId: string;
  /** Column the card sits in, so a drop in place can be ignored. */
  columnId: string;
  /** Refuses the drag, typically while a previous move is still in flight. */
  disabled?: boolean;
  /** Classes of the card surface. Composed with `twMerge`. */
  className?: string;
  /** Applied on top while this card is the one being dragged. */
  draggingClassName?: string;
  ariaLabel?: string;
  /** Click, or Enter on the keyboard. Not fired at the end of a drag. */
  onActivate?: () => void;
  children: React.ReactNode;
}

export const KanbanCard = ({
  cardId,
  columnId,
  disabled = false,
  className,
  draggingClassName = "opacity-40",
  ariaLabel,
  onActivate,
  children,
}: KanbanCardProps) => {
  const { enabled } = useContext(KanbanDndStateContext);
  const draggable = enabled && !disabled;
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: cardId,
    data: { columnId },
    disabled: !draggable,
  });

  /**
   * A drop that lands close to where it started still fires a click on the card,
   * which would open the detail behind the user's back. Ignore clicks for a
   * moment after a drag.
   */
  const swallowNextClick = useRef(false);
  useEffect(() => {
    if (isDragging) {
      swallowNextClick.current = true;
      return;
    }
    if (!swallowNextClick.current) return;
    const timer = window.setTimeout(() => {
      swallowNextClick.current = false;
    }, 150);
    return () => window.clearTimeout(timer);
  }, [isDragging]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (swallowNextClick.current) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }
    onActivate?.();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (draggable && !isIgnoredTarget(event.target)) {
      listeners?.onKeyDown?.(event);
    }
    if (event.defaultPrevented) return;
    if (event.key === "Enter" || (!draggable && event.key === " ")) {
      event.preventDefault();
      onActivate?.();
    }
  };

  return (
    <div
      ref={setNodeRef}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      className={twMerge(
        className,
        draggable &&
          "cursor-grab touch-pan-y select-none active:cursor-grabbing",
        isDragging && draggingClassName
      )}
    >
      {children}
    </div>
  );
};
